import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCatalog, fetchKits, prepareCheckout } from "../api/catalog";
import { useCart } from "../cart/CartContext";
import ProductCard from "../components/ProductCard";
import ProductGallery from "../components/ProductGallery";
import StoreBoot from "../components/StoreBoot";
import StoreCredit from "../components/StoreCredit";
import StoreHeader from "../components/StoreHeader";
import StorePixels from "../components/StorePixels";
import VariantPicker from "../components/VariantPicker";
import { useProductDetails } from "../hooks/useProductDetails";
import {
  formatInstallmentLine,
  formatMoney,
} from "../lib/money";
import { applyPageSeo, currentPageUrl, plainText } from "../lib/pageSeo";
import { sanitizeHtml } from "../lib/sanitizeHtml";
import { storePath } from "../lib/storePaths";

function stockDetails(variant) {
  if (!variant) return null;
  if (variant.stockStatus === "in_stock") {
    const qty = Number(variant.availableQuantity);
    return {
      label: "Em estoque",
      detail: qty > 0 && qty <= 10 ? `Restam ${qty} unidades` : null,
      tone: "available",
    };
  }
  if (variant.stockStatus === "backorder") {
    return {
      label: "Disponível sob encomenda",
      detail: null,
      tone: "backorder",
    };
  }
  return { label: "Produto indisponível", detail: null, tone: "unavailable" };
}

function ProductDescription({ value }) {
  const raw = String(value || "").trim();
  const looksHtml = /<\/?[a-z][\s\S]*?>/i.test(raw);
  if (!looksHtml) {
    return <p className="product-information__description">{raw}</p>;
  }
  const html = sanitizeHtml(raw);
  if (!html) {
    return (
      <p className="product-information__description">
        {raw.replace(/<[^>]+>/g, "")}
      </p>
    );
  }
  return (
    <div
      className="product-information__description product-information__description--html"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function asStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

export default function ProductDetailPage({ storeSlug, productHandle }) {
  const { addItem, addKit } = useCart();
  const { store, product, loading, error, retry } = useProductDetails(
    storeSlug,
    productHandle
  );
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [addedQuietly, setAddedQuietly] = useState(false);
  const [kits, setKits] = useState([]);
  const [selectedKitId, setSelectedKitId] = useState("");
  const [kitError, setKitError] = useState("");
  const [kitSubmitting, setKitSubmitting] = useState(false);
  const kitId = new URLSearchParams(window.location.search).get("kit");
  const affiliateCode = new URLSearchParams(window.location.search).get("aff") || "";
  const selectedKit = kits.find((item) => item.id === selectedKitId) || null;
  const [relatedProducts, setRelatedProducts] = useState([]);
  const confirmationTimerRef = useRef(null);
  const handleVariantChange = useCallback(
    (variant) => setSelectedVariant(variant),
    []
  );
  const availability = stockDetails(selectedVariant);
  const category = product?.categories?.[0];
  const storeUrl = storePath("", storeSlug);
  const cartUrl = storePath("/cart", storeSlug);
  const installmentLine = selectedVariant
    ? formatInstallmentLine(
        selectedVariant.amountMinor,
        selectedVariant.currency
      )
    : null;
  const formula = asStringList(product?.formula);
  const benefits = asStringList(product?.benefits);
  const dosage = String(product?.dosage || "").trim();
  const trustBadges = Array.isArray(store?.trustBadges)
    ? store.trustBadges.filter((badge) => String(badge?.label || "").trim())
    : [];
  const legalDisclaimer = String(store?.legalDisclaimer || "").trim();
  const outOfStock = selectedVariant?.stockStatus === "out_of_stock";

  useEffect(
    () => () => {
      window.clearTimeout(confirmationTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!product?.title) return;
    const description = plainText(product.description || product.subtitle || "");
    const image = product.primaryImage || product.images?.find((item) => item?.url)?.url || store?.logoUrl || "";
    applyPageSeo({
      title: `${product.title} | ${store?.name || "AtomicPay"}`.slice(0, 70),
      description: description.slice(0, 160),
      image: /^https:\/\//i.test(image) ? image : "",
      url: currentPageUrl(),
    });
  }, [product, store]);

  useEffect(() => {
    if (!storeSlug || !product?.id) {
      setRelatedProducts([]);
      return undefined;
    }
    const controller = new AbortController();
    fetchCatalog(storeSlug, {
      page: 1,
      limit: 8,
      filters: category?.id ? { category: category.id } : {},
      signal: controller.signal,
    })
      .then((result) => {
        const items = (result?.products || [])
          .filter((item) => item.id !== product.id)
          .slice(0, 4);
        setRelatedProducts(items);
      })
      .catch((requestError) => {
        if (requestError.name !== "AbortError") setRelatedProducts([]);
      });
    return () => controller.abort();
  }, [category?.id, product?.id, storeSlug]);

  useEffect(() => {
    if (!product?.id) return undefined;
    const controller = new AbortController();
    setKitError("");
    fetchKits(storeSlug, product.id, { signal: controller.signal })
      .then((rows) => {
        const available = Array.isArray(rows) ? rows : [];
        setKits(available);
        setSelectedKitId((current) => {
          if (available.some((row) => row.id === kitId)) return kitId;
          if (available.some((row) => row.id === current)) return current;
          return available[0]?.id || "";
        });
        if (kitId && !available.some((row) => row.id === kitId)) {
          setKitError("Este kit não está disponível. O preço abaixo é o preço unitário da loja.");
        }
      })
      .catch((requestError) => {
        if (requestError?.name === "AbortError") return;
        setKits([]);
        if (kitId) {
          setKitError("Este kit não está disponível. O preço abaixo é o preço unitário da loja.");
        }
      });
    return () => controller.abort();
  }, [kitId, product?.id, storeSlug]);

  const buyKit = async () => {
    if (!selectedKit || !selectedVariant || kitSubmitting) return;
    setKitSubmitting(true);
    setKitError("");
    try {
      const storageKey = `atomicpay-kit-checkout:${storeSlug}:${product.id}:${selectedKit.id}:${selectedVariant.id}:${affiliateCode}`;
      let idempotencyKey = sessionStorage.getItem(storageKey);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        sessionStorage.setItem(storageKey, idempotencyKey);
      }
      const checkout = await prepareCheckout(
        storeSlug,
        [{
          variantId: selectedVariant.id,
          quantity: 1,
          offerId: selectedKit.id,
          ...(affiliateCode ? { affiliateCode } : {}),
        }],
        { idempotencyKey }
      );
      const checkoutBaseUrl = String(
        import.meta.env.VITE_CHECKOUT_URL || "http://localhost:5174"
      ).replace(/\/$/, "");
      window.location.assign(
        `${checkoutBaseUrl}/commerce/${encodeURIComponent(checkout.checkoutToken)}`
      );
    } catch (checkoutError) {
      console.error(JSON.stringify({
        event: "commerce.checkout.prepare_failed",
        code: checkoutError?.code || null,
        status: checkoutError?.status || null,
        message: checkoutError?.message || null,
        offerId: selectedKit?.id || null,
        productId: product?.id || null,
      }));
      setKitError(checkoutError?.message || "Não foi possível abrir o checkout deste kit.");
      setKitSubmitting(false);
    }
  };

  const addSelectedKit = () => {
    if (!selectedKit || !addKit(product, selectedVariant, selectedKit, affiliateCode)) return;
    setAddedQuietly(true);
    window.clearTimeout(confirmationTimerRef.current);
    confirmationTimerRef.current = window.setTimeout(
      () => setAddedQuietly(false),
      1800
    );
  };

  const buyNow = () => {
    if (!addItem(product, selectedVariant)) return;
    window.location.assign(cartUrl);
  };

  const addAndContinue = () => {
    if (!addItem(product, selectedVariant)) return;
    setAddedQuietly(true);
    window.clearTimeout(confirmationTimerRef.current);
    confirmationTimerRef.current = window.setTimeout(
      () => setAddedQuietly(false),
      1800
    );
  };

  if (loading) return <StoreBoot />;

  return (
    <div className="storefront">
      <StorePixels store={store} product={product} />
      <StoreHeader store={store} storeSlug={storeSlug} />

      <main className="product-page">
        <nav className="breadcrumb" aria-label="Navegação estrutural">
          <a href={storeUrl}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="m15 18-6-6 6-6" />
            </svg>
            Todos os produtos
          </a>
          {product && <span aria-hidden>/</span>}
          {product && <span>{product.title}</span>}
        </nav>

        {error ? (
          <section className="state-card" role="alert">
            <h1>
              {error.status === 404
                ? "Produto não encontrado"
                : "Não foi possível carregar o produto"}
            </h1>
            <p>
              {error.status === 404
                ? "Este produto não está disponível ou o endereço está incorreto."
                : error.message}
            </p>
            <button type="button" onClick={retry}>
              Tentar novamente
            </button>
          </section>
        ) : (
          <>
            <div className="product-detail">
              <ProductGallery
                images={product.images}
                productTitle={product.title}
              />

              <section className="product-information">
                <div className="product-information__intro">
                  {(product.tags?.length > 0 || category) && (
                    <div className="product-information__meta">
                      {product.tags?.length > 0
                        ? product.tags.map((tag) => (
                            <span
                              key={tag.id || tag.handle || tag.name}
                              className="product-tag"
                              style={{
                                color: tag.color || "var(--accent)",
                                backgroundColor: `color-mix(in srgb, ${tag.color || "var(--accent)"} 16%, white)`,
                              }}
                            >
                              {tag.name}
                            </span>
                          ))
                        : category && (
                            <span className="product-tag product-tag--fallback">
                              {category.name}
                            </span>
                          )}
                    </div>
                  )}
                  <h1>{product.title}</h1>
                  {product.subtitle && (
                    <p className="product-information__subtitle">
                      {product.subtitle}
                    </p>
                  )}
                  {product.brand && (
                    <p className="product-information__brand">{product.brand}</p>
                  )}
                  {product.description && (
                    <ProductDescription value={product.description} />
                  )}
                </div>

                {product.variants.length > 0 && (
                  <div className="product-purchase-details">
                    <VariantPicker
                      variants={product.variants}
                      onChange={handleVariantChange}
                    />

                    {kits.length > 0 && selectedVariant && (
                      <div className="product-buy-card product-buy-card--kits">
                        <div className="kit-choice-list" role="listbox" aria-label="Kits">
                          {kits.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              className="kit-choice"
                              role="option"
                              aria-selected={item.id === selectedKit?.id}
                              onClick={() => setSelectedKitId(item.id)}
                            >
                              <span>Kit de {item.quantity}</span>
                              <strong>{formatMoney(item.priceMinor, selectedVariant.currency)}</strong>
                            </button>
                          ))}
                        </div>
                        {selectedKit && (
                          <>
                            <div className="product-buy-card__pricing">
                              <span className="product-buy-card__label">Kit de {selectedKit.quantity}</span>
                              <p className="product-buy-card__price">
                                {formatMoney(selectedKit.priceMinor, selectedVariant.currency)}
                              </p>
                              <span className="product-detail-price__installments">
                                {selectedKit.freeShipping
                                  ? "Frete grátis neste kit. O pedido cobra R$ 37,50 uma vez se outro kit do carrinho não tiver frete grátis."
                                  : `Frete de ${formatMoney(selectedKit.shippingMinor || 3750, "BRL")} uma vez no pedido. O frete não entra na partilha.`}
                              </span>
                            </div>
                            <div className="kit-choice-actions">
                              <button
                                type="button"
                                className="add-to-cart-button product-buy-card__cta"
                                disabled={outOfStock}
                                onClick={addSelectedKit}
                              >
                                {addedQuietly ? "Kit adicionado ao carrinho" : "Adicionar este kit"}
                              </button>
                              <button
                                type="button"
                                className="product-continue-link"
                                disabled={outOfStock || kitSubmitting}
                                onClick={buyKit}
                              >
                                {kitSubmitting ? "Abrindo checkout..." : "Pagar este kit agora"}
                              </button>
                            </div>
                            <p className="product-information__subtitle">
                              O carrinho cobra o preço deste kit. Os botões de preço unitário abaixo não usam o kit.
                              {affiliateCode ? " Este acesso veio de um link de afiliado. O preço não muda." : ""}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    {kitError && <p className="product-information__subtitle">{kitError}</p>}
                    {selectedVariant && (
                      <>
                        <div className="product-buy-card">
                          <div className="product-buy-card__pricing">
                            <span className="product-buy-card__label">Preço</span>
                            {selectedVariant.compareAtAmountMinor != null &&
                              Number(selectedVariant.compareAtAmountMinor) >
                                Number(selectedVariant.amountMinor) && (
                                <span className="product-detail-price__compare">
                                  {formatMoney(
                                    selectedVariant.compareAtAmountMinor,
                                    selectedVariant.currency
                                  )}
                                </span>
                              )}
                            <p className="product-buy-card__price">
                              {formatMoney(
                                selectedVariant.amountMinor,
                                selectedVariant.currency
                              )}
                            </p>
                            {installmentLine ? (
                              <span className="product-detail-price__installments">
                                {installmentLine}
                              </span>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            className="add-to-cart-button product-buy-card__cta"
                            disabled={outOfStock}
                            onClick={buyNow}
                          >
                            {outOfStock ? "Produto indisponível" : "Comprar agora"}
                          </button>
                        </div>

                        <div className="selected-variant-summary">
                          <div>
                            <span
                              className={`stock-dot stock-dot--${availability?.tone}`}
                            />
                            <strong>{availability?.label}</strong>
                            {availability?.detail && (
                              <small>{availability.detail}</small>
                            )}
                          </div>
                        </div>

                        {!outOfStock && (
                          <button
                            type="button"
                            className="product-continue-link"
                            onClick={addAndContinue}
                          >
                            {addedQuietly
                              ? "Adicionado — continue comprando"
                              : "Adicionar e continuar comprando"}
                          </button>
                        )}

                        {trustBadges.length > 0 && (
                          <ul className="product-trust-badges">
                            {trustBadges.slice(0, 3).map((badge, index) => (
                              <li key={`${badge.label}-${index}`}>
                                <span className="product-trust-badges__icon" aria-hidden>
                                  <svg viewBox="0 0 24 24">
                                    {index === 0 ? (
                                      <path d="M3 7h11v10H3zm11 3h4l3 3v4h-7zM7 20a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm10 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
                                    ) : index === 1 ? (
                                      <path d="M12 3 4 6v6c0 5 3.4 8.4 8 9 4.6-.6 8-4 8-9V6l-8-3zm-1 12-3.5-3.5 1.4-1.4L11 12.2l4.1-4.1 1.4 1.4L11 15z" />
                                    ) : (
                                      <path d="M9 7H5V3m10 14h4v4M5 7l5.5 5.5M19 17l-5.5-5.5M14 7h5v5M10 17H5v-5" />
                                    )}
                                  </svg>
                                </span>
                                <span>{badge.label}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                )}
              </section>
            </div>

            {(formula.length > 0 || benefits.length > 0 || dosage) && (
              <section className="product-content-blocks">
                {(formula.length > 0 || benefits.length > 0) && (
                  <div className="product-content-blocks__grid">
                    {formula.length > 0 && (
                      <div className="product-content-panel">
                        <p className="product-content-panel__eyebrow">Fórmula</p>
                        <h2>O que tem dentro</h2>
                        <ul>
                          {formula.map((item) => (
                            <li key={item}>
                              <span aria-hidden>✓</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {benefits.length > 0 && (
                      <div className="product-content-panel">
                        <p className="product-content-panel__eyebrow">Por que usar</p>
                        <h2>Benefícios</h2>
                        <ul>
                          {benefits.map((item) => (
                            <li key={item}>
                              <span aria-hidden>✦</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {dosage && (
                  <div className="product-dosage">
                    <p className="product-dosage__label">Posologia</p>
                    <p>{dosage}</p>
                  </div>
                )}
              </section>
            )}

            {relatedProducts.length > 0 && (
              <section className="product-related">
                <div className="product-related__heading">
                  <p>Você também pode gostar</p>
                  <h2>Outros produtos</h2>
                </div>
                <div className="product-grid">
                  {relatedProducts.map((item) => (
                    <ProductCard
                      key={item.id}
                      product={item}
                      storeSlug={storeSlug}
                    />
                  ))}
                </div>
              </section>
            )}

            {legalDisclaimer && (
              <aside className="product-legal-disclaimer">
                <strong>Aviso</strong>
                <p>{legalDisclaimer}</p>
              </aside>
            )}
          </>
        )}
      </main>

      <footer className="store-footer">
        <StoreCredit />
      </footer>
    </div>
  );
}
